// src/prototype/pages/EAPersonalizationPage.tsx
// ============================================================
// EA STEP 1: PERSONALIZE
//
// Visual chrome matches Evaluate (journey bar, identity header).
// Profile / surveys / documents still load via useEAPersonalizationProgress
// for EA_CLIENT_ID. Advanced weights stay on /ea/weights.
//
// Route: /ea/personalization
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { EAPersonalizationCard } from '../components/EAPersonalizationCard';
import { AdvancedPersonalizationBand } from '../components/AdvancedPersonalizationBand';
import { EASurveyOverviewModal } from '../components/EASurveyOverviewModal';
import { HelpPopover } from '../components/help/HelpPopover';
import { useAuth } from '@/contexts/AuthContext';
import { useEA } from '../contexts/EAContext';
import { useEAPersonalizationProgress } from '../hooks/useEAPersonalizationProgress';
import {
  calculateOverallPersonalizationStrength,
  initiativesScoreFromFinalistCount,
} from '../utils/personalizationProgress';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import { EA_CLIENT_ID } from '../constants/eaClient';
import { fetchOpportunities, UCE_CATALOG_FILTERS } from '../services/useCaseExplorerService';
import { fetchSurveyFreshness } from '../services/surveyFreshnessService';
import type { SurveyFreshnessSummary } from '../utils/surveyFreshness';

function strengthMeta(pct: number): { label: string; color: string } {
  if (pct >= 80) return { label: 'Excellent', color: '#15803d' };
  if (pct >= 60) return { label: 'Good', color: '#18365E' };
  if (pct >= 40) return { label: 'Moderate', color: '#FF6E4C' };
  if (pct >= 20) return { label: 'Weak', color: '#d97706' };
  return { label: 'Minimal', color: '#64748b' };
}

export default function EAPersonalizationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, sessionLoading } = useAuth();
  const { isPro } = useDemoPlan();
  const { finalistIds } = useEA();
  const initiativesScore = initiativesScoreFromFinalistCount(finalistIds.size);
  const userId = session?.user_id || 'admin';

  const [refreshKey, setRefreshKey] = useState(0);
  const { personalizationData, progressLoading: isLoadingProgress } = useEAPersonalizationProgress(
    EA_CLIENT_ID,
    initiativesScore,
    refreshKey,
  );

  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyFreshness, setSurveyFreshness] = useState<SurveyFreshnessSummary | null>(null);

  useEffect(() => {
    void fetchOpportunities(EA_CLIENT_ID, UCE_CATALOG_FILTERS);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSurveyFreshness(EA_CLIENT_ID, userId)
      .then((summary) => {
        if (!cancelled) setSurveyFreshness(summary);
      })
      .catch((err) => {
        console.warn('[EAPersonalizationPage] Survey freshness fetch failed:', err);
        if (!cancelled) setSurveyFreshness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  const overallPercentage = calculateOverallPersonalizationStrength(personalizationData);
  const strength = strengthMeta(overallPercentage);

  useEffect(() => {
    const state = location.state as { restoreScrollY?: number; refresh?: boolean } | null;
    const shouldRefresh =
      state?.refresh === true || typeof state?.restoreScrollY === 'number';
    if (!shouldRefresh) return;

    if (typeof state.restoreScrollY === 'number') {
      setTimeout(() => {
        window.scrollTo(0, state.restoreScrollY);
      }, 100);
    }
    setRefreshKey((prev) => prev + 1);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  const handleProfileClick = () => {
    navigate('/client-profile', {
      state: {
        fromEA: true,
        clientId: EA_CLIENT_ID,
        returnTo: '/ea/personalization',
        restoreScrollY: window.scrollY,
      },
    });
  };

  const handleSurveysClick = () => {
    setIsSurveyModalOpen(true);
  };

  const handleDocumentsClick = () => {
    navigate('/documents?fromEA=true', {
      state: {
        fromEA: true,
        clientId: EA_CLIENT_ID,
        returnTo: '/ea/personalization',
        restoreScrollY: window.scrollY,
      },
    });
  };

  const handleSurveyModalClose = () => {
    setIsSurveyModalOpen(false);
    setRefreshKey((prev) => prev + 1);
  };

  const handleSurveyProgressUpdate = () => {
    setRefreshKey((prev) => prev + 1);
  };

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4" />
          <p className="text-[#717182]">Loading your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-2">
        <EAJourneyBar />
      </div>

      <main className="max-w-[1280px] mx-auto px-6 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          <div className="p-6 pb-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">
                    Your Inputs
                  </span>
                  <HelpPopover helpKey="personalization_strength" />
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ color: strength.color, backgroundColor: `${strength.color}18` }}
                  >
                    {isLoadingProgress ? 'Loading' : strength.label}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-[#18365E] leading-tight">Personalize</h1>
                <p className="text-sm text-[#717182] mt-1 max-w-2xl">
                  Your organizational inputs shape which opportunities surface, how they are scored, and how defensible the ranking is. You can refine these at any time.
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {import.meta.env.DEV && (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('dva_demo_plan_override', 'freemium');
                      window.location.reload();
                    }}
                    className="text-[10px] text-[#717182]/50 hover:text-[#717182] transition-colors"
                  >
                    Reset Demo
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/ea/opportunities')}
                  className="bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors"
                >
                  Continue to Opportunities
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <EAPersonalizationCard
            type="profile"
            percentage={personalizationData.profile}
            onClick={handleProfileClick}
            isLoading={isLoadingProgress}
            helpKey="organization_profile"
          />
          <EAPersonalizationCard
            type="surveys"
            percentage={personalizationData.surveys}
            onClick={handleSurveysClick}
            isLoading={isLoadingProgress}
            helpKey="data_intake_surveys"
            surveyFreshness={surveyFreshness}
          />
          <EAPersonalizationCard
            type="documents"
            percentage={personalizationData.documents}
            onClick={handleDocumentsClick}
            isLoading={isLoadingProgress}
            helpKey="document_upload"
          />
        </div>

        <AdvancedPersonalizationBand
          isPro={isPro}
          onUpgrade={() => navigate('/ea/upgrade')}
        />
      </main>

      <EASurveyOverviewModal
        isOpen={isSurveyModalOpen}
        onClose={handleSurveyModalClose}
        onProgressUpdate={handleSurveyProgressUpdate}
      />
    </div>
  );
}
